import React, { useState, useEffect, useCallback } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import AssistantSetup from "../components/AssistantSetup";
import JobApplicationStats from "../components/JobApplicationStats";
import CustomModal from "../components/CustomModal";
import styles from "../styles/dashboard.module.css";
import { useAuth } from "../context/AuthContext";
import { getUserJobInfo } from "../utils/api";
import { ResumeBuilderProvider } from "../context/ResumeBuilderContext";
import ResumeBuilderPage from "./ResumeBuilderPage";

const SIDEBAR_OPEN_WIDTH = "250px";
const SIDEBAR_CLOSED_WIDTH = "70px";
const AUTO_REFRESH_INTERVAL = 5 * 60 * 1000;

const SESSION_CAP = 10;
const DAILY_CAP = 40;

const DashboardContent = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [modalMessage, setModalMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const [userJobData, setUserJobData] = useState({
    jobHistory: [],
    availableJobs: 0,
    isPremium: false,
    dailyCount: 0,
    monthlyCount: 0,
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  const fetchStats = useCallback(
    async (isInitialLoad = false) => {
      if (!user?.email) return;
      if (isInitialLoad) setIsLoadingStats(true);
      try {
        const response = await getUserJobInfo(user.email);
        if (response.data.success) {
          const userData = response.data.user;

          const now = new Date();
          const jobHistory = Array.isArray(userData.jobHistory)
            ? userData.jobHistory
            : [];
          let calculatedDailyCount = 0;
          let calculatedMonthlyCount = 0;

          jobHistory.forEach((job) => {
            const jobDate = job.appliedAt?._seconds
              ? new Date(job.appliedAt._seconds * 1000)
              : new Date(job.appliedAt);
            const diffHours = (now - jobDate) / (1000 * 60 * 60);
            if (diffHours < 24) calculatedDailyCount++;
            if (diffHours < 720) calculatedMonthlyCount++;
          });

          setUserJobData({
            ...userData,
            jobHistory,
            dailyCount: calculatedDailyCount,
            monthlyCount: calculatedMonthlyCount,
          });
        }
      } catch (error) {
        console.error("Failed to fetch user stats:", error);
      } finally {
        if (isInitialLoad) setIsLoadingStats(false);
      }
    },
    [user]
  );

  useEffect(() => {
    fetchStats(true);
    const intervalId = setInterval(
      () => fetchStats(false),
      AUTO_REFRESH_INTERVAL
    );
    return () => clearInterval(intervalId);
  }, [fetchStats]);

  useEffect(() => {
    const handleExtensionMessage = (event) => {
      if (event.source !== window || event.data.type !== "EXTENSION_FEEDBACK")
        return;

      const { status, message } = event.data.payload;
      console.log(`Extension says: [${status}] ${message}`);

      if (status === "STATUS_UPDATE") {
        setStatusMessage(message);
      } else if (status === "STATS_REFRESH") {
        fetchStats(false);
        setStatusMessage("Application Logged. Updating stats...");
      } else if (status === "COMPLETED") {
        setIsProcessing(false);
        setStatusMessage("");
        setModalMessage(`Automation Finished: ${message}`);
        setShowModal(true);
        fetchStats(true);
      } else if (status === "ERROR") {
        setIsProcessing(false);
        setStatusMessage("");
        setModalMessage(`Error: ${message}`);
        setShowModal(true);
      }
    };

    window.addEventListener("message", handleExtensionMessage);
    return () => window.removeEventListener("message", handleExtensionMessage);
  }, [fetchStats]);

  const startAutomation = () => {
    if (userJobData.dailyCount >= DAILY_CAP) {
      setModalMessage(
        `Daily limit reached (${DAILY_CAP}/day). Please come back tomorrow.`
      );
      setShowModal(true);
      return;
    }

    if (userJobData.availableJobs <= 0) {
      navigate("/pricing");
      setModalMessage("No job credits remaining. Please upgrade to continue.");
      setShowModal(true);
      return;
    }

    const settingsRaw = localStorage.getItem("wellfoundSettings");
    if (!settingsRaw) {
      navigate("/dashboard/setup");
      setModalMessage("Please complete 'Assistant Setup' first.");
      setShowModal(true);
      return;
    }

    const settings = JSON.parse(settingsRaw);

    const finalProfile = {
      ...settings,
      email: user.email,
    };

    if (!finalProfile.jobRole) {
      setModalMessage("Job Role is missing in Setup.");
      setShowModal(true);
      return;
    }

    const dailyRemaining = DAILY_CAP - userJobData.dailyCount;
    const creditsRemaining = userJobData.availableJobs;

    const limitForThisSession = Math.min(
      SESSION_CAP,
      dailyRemaining,
      creditsRemaining
    );

    if (limitForThisSession <= 0) {
      setModalMessage("Cannot start session. Check your limits.");
      setShowModal(true);
      return;
    }

    setIsProcessing(true);
    setStatusMessage(`Initializing session for ${limitForThisSession} jobs...`);

    window.postMessage(
      {
        type: "START_WELLFOUND_AUTOMATION",
        payload: {
          searchQuery: {
            role: finalProfile.jobRole,
            location: finalProfile.location,
          },
          userProfile: finalProfile,
          limit: limitForThisSession,
        },
      },
      window.location.origin
    );
  };

  const launchState = {
    text: isProcessing ? statusMessage || "Processing..." : "Start Automation",
  };

  return (
    <>
      {showModal && (
        <CustomModal
          message={modalMessage}
          onConfirm={() => setShowModal(false)}
        />
      )}

      {location.pathname === "/dashboard" && (
        <div className={styles.pageContainer}>
          <div className={styles.mainCard}>
            <div className={styles.statsSection}>
              <JobApplicationStats
                jobData={userJobData}
                isLoading={isLoadingStats}
                startAutomation={startAutomation}
                isLaunchDisabled={
                  isProcessing || userJobData.availableJobs <= 0
                }
                launchState={launchState}
              />
            </div>
          </div>
        </div>
      )}

      <Routes>
        <Route
          path="setup"
          element={
            <AssistantSetup
              startAutomation={startAutomation}
              isLaunchDisabled={isProcessing}
              launchState={launchState}
            />
          }
        />
        <Route path="resume-builder" element={<ResumeBuilderPage />} />
      </Routes>
    </>
  );
};

const Dashboard = () => {
  const { logout: contextLogout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  return (
    <ResumeBuilderProvider>
      <div className={styles.dashboardLayout}>
        <Sidebar
          isOpen={isSidebarOpen}
          toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
          handleLogout={contextLogout}
        />
        <div
          className={styles.dashboardContent}
          style={{
            marginLeft: isSidebarOpen
              ? SIDEBAR_OPEN_WIDTH
              : SIDEBAR_CLOSED_WIDTH,
          }}
        >
          <DashboardContent />
        </div>
      </div>
    </ResumeBuilderProvider>
  );
};

export default Dashboard;
